import { getUploadJob, updateUploadJob } from "./uploadPersistence";

function getTaskResult(task) {
  if (!task) return Promise.resolve(null);
  return Promise.resolve(task.result || task);
}

function callTaskMethod(task, method) {
  if (task && typeof task[method] === "function") {
    return task[method]();
  }
  return null;
}

export function createUploadJobSession({ db, jobId, taskFactory } = {}) {
  if (!db) {
    throw new Error("An upload persistence database is required.");
  }
  if (!jobId) {
    throw new Error("An upload job id is required.");
  }

  let task = null;
  let runPromise = null;

  async function setJobStatus(status, extra = {}) {
    const record = await getUploadJob({ db, jobId });
    if (!record) return null;
    const updatedJob = {
      ...record.job,
      ...extra,
      status,
      updatedAt: new Date().toISOString()
    };
    await updateUploadJob({ db, job: updatedJob });
    return updatedJob;
  }

  async function run() {
    if (runPromise) return runPromise;
    if (typeof taskFactory !== "function") {
      throw new Error("An upload task factory is required to run an upload.");
    }

    runPromise = (async () => {
      const record = await getUploadJob({ db, jobId });
      if (!record || record.job.status === "cancelled") {
        return record;
      }

      await setJobStatus("running");
      task = taskFactory({
        job: record.job,
        sources: record.sources
      });

      try {
        const result = await getTaskResult(task);
        const completedJob = await setJobStatus("uploaded", {
          uploadedAt: new Date().toISOString(),
          completedBytes: record.job.totalBytes
        });
        return { job: completedJob, result };
      } catch (error) {
        const latest = await getUploadJob({ db, jobId });
        if (
          (error && error.name === "UploadCancelledError") ||
          (latest && ["paused", "cancelled"].includes(latest.job.status))
        ) {
          return latest;
        }
        await setJobStatus("failed", {
          error: error && error.message ? error.message : "Upload failed."
        });
        throw error;
      } finally {
        task = null;
        runPromise = null;
      }
    })();

    return runPromise;
  }

  return {
    start: run,
    resume() {
      if (task) {
        callTaskMethod(task, "resume");
        setJobStatus("running").catch(() => {});
        return runPromise;
      }
      return run();
    },
    async pause() {
      await Promise.resolve(callTaskMethod(task, "pause"));
      return setJobStatus("paused");
    },
    async cancel() {
      await Promise.resolve(callTaskMethod(task, "cancel"));
      return setJobStatus("cancelled", {
        cancelledAt: new Date().toISOString()
      });
    },
    getTask() {
      return task;
    }
  };
}
